import { useAppConfig } from "~/app/config/useAppConfig";
import { useDispatch } from "react-redux";

import ProductImage from "~/domains/product/components/ProductImage";
import { Box, Card, Collapse, IconButton, Stack, styled, useTheme } from "~/common/mui";
import { useAddProductToWishlistFlow } from "~/features/wishlist/hooks/useAddProductToWishlistFlow";
import { type AppDispatch } from "~/app/store";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { useRemoveProductFromComparisonMutation } from "~/features/compareProducts/api/productComparisonQuery";
import { trackAddToComparisonClick } from "~/features/compareProducts/utils/analytics";
import { AddToCartService } from "~/features/addToCart/AddToCartService";
import { canSeeAddToCartButton } from "~/utils/features";
import { useTranslation } from "react-i18next";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import { Product } from "~/domains/product/Product";
import { PriceElements, type PriceProps } from "~/domains/pricing/components/Price/PriceV4";
import { SaleDiscountIndicator } from "~/domains/salesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import { C_PRIMARY, C_WHITE } from "~/common/components/icon/Colors";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { getInStock } from "~/utils/packageEntryUtils";
import StockIndicator from "~/domains/product/components/StockIndicator/StockIndicator";
import { TooltipWithArrow } from "~/common/components/TooltipWithArrow";
import { useProductComparisonCardLogic } from "~/features/compareProducts/hooks/useProductComparisonCardLogic";

const WIDTHS = {
  product: "82px",
  gap: "4px",
  brand: "32px",
  manufacturer: "32px",
};

interface Props {
  productCode: string;
  smallCardView: boolean;
  handlePin: (productCode: string) => void;
  isPinned: boolean;
}

const ProductComparisonCard = ({ productCode, smallCardView, handlePin, isPinned }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const removeMutation = useRemoveProductFromComparisonMutation();

  const { price, oldPrice, netPrice, discountFormatted, productDescriptor, packCode, tariffSurcharge, hasSurcharge } =
    useProductComparisonCardLogic(productCode);

  const wishlistButtonVisible = userHasPermission(Permission.ADD_TO_WISHLIST);
  const atcButtonVisible = canSeeAddToCartButton();

  const handleCloseClick = async () => {
    if (isPinned) {
      handlePin(productCode);
    }
    try {
      await removeMutation.mutateAsync(productCode);
      trackAddToComparisonClick(productDescriptor.base.designation, false, "Product Comparison");
    } catch (error) {
      console.error("Failed to remove product from comparison", error);
    }
  };

  const doAddToCart = () => {
    AddToCartService.openAddToCartWizard(
      {
        productCode: productDescriptor.base.code,
        restrictionQuery: "",
      },
      {
        page: "COMPARISON",
      },
      dispatch,
    );
  };

  const [openAddToWishlist, addToWishlistModal] = useAddProductToWishlistFlow({ productInput: productDescriptor.base });
  const onWishlistBtnClick = () => {
    openAddToWishlist();
  };

  const salesHelper = useSalesHelper(productDescriptor);
  const isProductOnSale = salesHelper.isProductOnSale();
  const isPackageOnSale = salesHelper.isPackageOnSale(packCode);
  const stock =
    productDescriptor.availability?.packagingInformation.reduce((acc, pack) => getInStock(pack) + acc, 0) ?? 0;

  const disableWS = useAppConfig()?.configuration.disableAllWS ?? false;

  const showPrices = !disableWS && userHasPermission(Permission.PRICE);
  const stockVisible = userHasPermission(Permission.AVAILABILITY) && productDescriptor.base.quantityVisible;
  let atcButton;
  let atwButton;

  if (atcButtonVisible) {
    atcButton = (
      <TooltipWithArrow title={t("comparision.overlay.card.atcTootlip")} placement="top">
        <IconButton
          size={"medium"}
          onClick={doAddToCart}
          disabled={disableWS}
          sx={{
            color: C_WHITE,
            bgcolor: C_PRIMARY,
            ":hover": {
              color: C_WHITE,
              bgcolor: C_PRIMARY,
            },
          }}
        >
          <ShoppingCartOutlinedIcon />
        </IconButton>
      </TooltipWithArrow>
    );
  }

  if (wishlistButtonVisible) {
    atwButton = (
      <TooltipWithArrow title={t("comparision.overlay.card.atwTootlip")} placement="top">
        <IconButton onClick={onWishlistBtnClick} variant="contained" color={"secondary"}>
          <BookmarkBorderOutlinedIcon />
        </IconButton>
      </TooltipWithArrow>
    );
  }

  return (
    <>
      {addToWishlistModal}
      <ProductDescriptorContext value={productDescriptor}>
        <ProductCard smallCardView={smallCardView}>
          {isProductOnSale && <FloatingSalesTag />}

          <Stack gap={1}>
            <Stack direction={"row"} justifyContent="space-between" alignItems={"flex-start"}>
              <FadingCollapse
                visible={!smallCardView}
                in={!smallCardView}
                timeout={theme.transitions.duration.standard}
                easing={theme.transitions.easing.easeInOut}
              >
                <ProductImage product={productDescriptor.base} widths={WIDTHS} />
              </FadingCollapse>
              <Stack direction={"row"} gap={1}>
                <TooltipWithArrow
                  title={
                    isPinned ? t("comparision.overlay.card.unpinTootlip") : t("comparision.overlay.card.pinTootlip")
                  }
                  placement="top"
                >
                  <StyledPinIcon size="tiny" onClick={() => handlePin(productCode)} className="pin-icon">
                    {isPinned ? <PushPinIcon /> : <PushPinOutlinedIcon />}
                  </StyledPinIcon>
                </TooltipWithArrow>
                <IconButton onClick={handleCloseClick} size="tiny">
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Stack>
            <Stack gap={0.5}>
              <Stack direction="row" alignItems="center">
                <Product.PreferredIndicator />
                <Product.PdpLink>
                  <Product.DesignationV2 variant="titleLarge" component={"b"} />
                </Product.PdpLink>
              </Stack>
              <Product.ShortDescriptionV2
                truncate
                sx={{
                  color: "text.secondary",
                }}
              />
            </Stack>
          </Stack>
          {showPrices && (
            <FadingCollapse
              visible={!smallCardView}
              in={!smallCardView}
              timeout={theme.transitions.duration.standard}
              easing={theme.transitions.easing.easeInOut}
            >
              <Stack>
                <Stack direction="row" gap={1} alignItems={"center"}>
                  <CurrentPrice
                    price={price}
                    zeroText={t("product.priceOnRequest")}
                    tariffSurcharge={tariffSurcharge}
                    variant="bodyLarge"
                    fontWeight={"bold"}
                    surchargeEnabled={hasSurcharge}
                  />
                  {isPackageOnSale && (
                    <SaleDiscountIndicator.V2 discountFormatted={`-${discountFormatted}`} sx={{ fontWeight: 400 }} />
                  )}
                </Stack>
                {oldPrice && <OldPrice price={oldPrice} />}
                <PriceElements.NetPriceLabel price={netPrice} />
              </Stack>
            </FadingCollapse>
          )}
          <FadingCollapse
            visible={!smallCardView}
            in={!smallCardView}
            timeout={theme.transitions.duration.standard}
            easing={theme.transitions.easing.easeInOut}
          >
            {stockVisible && <Box sx={{ alignSelf: "flex-start" }}>{<StockIndicator.V2 stock={stock} />}</Box>}
          </FadingCollapse>
          <FadingCollapse
            visible={!smallCardView}
            in={!smallCardView}
            timeout={theme.transitions.duration.standard}
            easing={theme.transitions.easing.easeInOut}
            sx={{ mt: "auto" }}
          >
            <Stack direction="row" gap={1}>
              {atwButton}
              {atcButton}
            </Stack>
          </FadingCollapse>
        </ProductCard>
      </ProductDescriptorContext>
    </>
  );
};

export default ProductComparisonCard;

const FadingCollapse = styled(Collapse, {
  shouldForwardProp: (prop) => prop !== "visible",
})<{ visible: boolean }>(({ visible }) => ({
  opacity: visible ? 1 : 0,
  transitionProperty: "height, opacity",
  willChange: "height, opacity",
}));

const ProductCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "smallCardView",
})<{ smallCardView: boolean }>(({ smallCardView, theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: smallCardView ? 0 : "16px",
  padding: "16px",
  width: "242px",
  minWidth: "242px",
  position: "relative",
  transition: theme.transitions.create("gap", {
    duration: theme.transitions.duration.standard,
    easing: theme.transitions.easing.easeInOut,
  }),
  [`&:hover .pin-icon`]: {
    opacity: 1,
    visibility: "visible",
  },
}));

const FloatingSalesTag = styled(SaleTag)({
  position: "absolute",
  top: 0,
  left: 0,
  height: "32px",
  width: "32px",
  zIndex: "10000",
});

const CurrentPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  fontWeight: soldOut ? "normal" : "bold",
  color: soldOut ? theme.palette.text.secondary : theme.palette.text.primary,
  textDecoration: soldOut ? "line-through" : "none",
}));

const OldPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  color: soldOut ? theme.palette.text.primary : theme.palette.text.secondary,
  fontWeight: soldOut ? "bold" : "normal",
  textDecoration: soldOut ? "none" : "line-through",
}));

const StyledPinIcon = styled(IconButton)(({ theme: _theme }) => ({
  opacity: 0,
  visibility: "hidden",
}));
